/**
 * Cross-app context utilities
 * URL parsing, system prompt building, and sessionStorage-based XACML transfer
 */

import type { StartConversationRequest } from '@/types/polix-ai'
import type { DiscoveryScan, DiscoveryResult } from '@/types/discovery'

const XACML_STORAGE_KEY = 'polix:xacml-for-policy-creation'

// ============================================================================
// URL PARAMETER TYPES
// ============================================================================

export interface CrossAppParams {
  readonly context: string | null
  readonly scanId: string | null
  readonly connectionName: string | null
  readonly tableId: string | null
  readonly tableName: string | null
  readonly columnName: string | null
  readonly classificationLevel: string | null
}

// ============================================================================
// URL PARSING
// ============================================================================

/**
 * Parse cross-app navigation parameters from URL search params
 */
export function parseCrossAppParams(searchParams: URLSearchParams): CrossAppParams {
  return {
    context: searchParams.get('context'),
    scanId: searchParams.get('scanId'),
    connectionName: searchParams.get('connectionName'),
    tableId: searchParams.get('tableId'),
    tableName: searchParams.get('tableName'),
    columnName: searchParams.get('columnName'),
    classificationLevel: searchParams.get('classificationLevel'),
  }
}

/**
 * Build a URL for navigating to Polix AI with discovery context
 */
export function buildPolixAIUrl(params: {
  readonly scanId: string
  readonly connectionName: string
}): string {
  const searchParams = new URLSearchParams({
    context: 'discovery',
    scanId: params.scanId,
    connectionName: params.connectionName,
  })
  return `/polix-ai?${searchParams.toString()}`
}

/**
 * Build a URL for navigating to Polix AI with data catalog context
 */
export function buildCatalogPolicyAIUrl(params: {
  readonly tableId: string
  readonly tableName: string
  readonly columnName?: string
  readonly classificationLevel?: string
}): string {
  const searchParams = new URLSearchParams({
    context: 'data-catalog',
    tableId: params.tableId,
    tableName: params.tableName,
  })
  if (params.columnName) searchParams.set('columnName', params.columnName)
  if (params.classificationLevel) searchParams.set('classificationLevel', params.classificationLevel)
  return `/polix-ai?${searchParams.toString()}`
}

// ============================================================================
// SYSTEM PROMPT BUILDING
// ============================================================================

/**
 * Build a contextual system prompt from scan data and results
 */
export function buildDiscoveryScanSystemPrompt(
  scan: DiscoveryScan,
  results: readonly DiscoveryResult[]
): string {
  const connectionName = scan.connection?.name ?? 'Unknown connection'
  const dbType = scan.connection?.databaseType ?? 'unknown'

  const sensitiveResults = results.filter(
    (r) => r.classificationLevel !== 'PUBLIC' && r.classificationLevel !== 'INTERNAL'
  )

  const flaggedResults = results.filter((r) => r.regulatoryFlags.length > 0)

  const levelCounts: Record<string, number> = {}
  for (const r of results) {
    levelCounts[r.classificationLevel] = (levelCounts[r.classificationLevel] ?? 0) + 1
  }

  const uniqueFlags = [...new Set(results.flatMap((r) => r.regulatoryFlags))]

  const summaryLines = [
    `Database: ${connectionName} (${dbType})`,
    `Total columns scanned: ${scan.columnsScanned}`,
    `Classified: ${scan.columnsClassified}`,
    `Flagged as sensitive: ${results.filter((r) => r.regulatoryFlags.length > 0).length}`,
    '',
    'Classification breakdown:',
    ...Object.entries(levelCounts).map(([level, count]) => `  - ${level}: ${count}`),
  ]

  if (uniqueFlags.length > 0) {
    summaryLines.push('', `Regulatory flags detected: ${uniqueFlags.join(', ')}`)
  }

  const topSensitive = sensitiveResults.slice(0, 15).map(
    (r) =>
      `  - ${r.schemaName}.${r.tableName}.${r.columnName} (${r.classificationLevel}, ${r.classificationLabel}, confidence: ${Math.round(r.confidence * 100)}%${r.regulatoryFlags.length > 0 ? `, flags: ${r.regulatoryFlags.join('/')}` : ''})`
  )

  const prompt = [
    'You are analyzing data discovery scan results to help create XACML access control policies.',
    '',
    'SCAN SUMMARY:',
    ...summaryLines,
    '',
    ...(topSensitive.length > 0
      ? [
          'TOP SENSITIVE COLUMNS:',
          ...topSensitive,
          ...(sensitiveResults.length > 15 ? [`  ... and ${sensitiveResults.length - 15} more`] : []),
        ]
      : []),
    ...(flaggedResults.length > 0
      ? [
          '',
          `${flaggedResults.length} column(s) have regulatory flags that may require specific access controls.`,
        ]
      : []),
    '',
    'Help the user create appropriate XACML policies to protect this sensitive data.',
    'Focus on the most critical data first (RESTRICTED, TOP_SECRET) and any regulatory-flagged columns.',
  ].join('\n')

  return prompt
}

/**
 * Build a contextual system prompt from data catalog table and columns
 */
export function buildCatalogSystemPrompt(
  tableName: string,
  columns: readonly { columnName: string; dataType: string; classificationLevel?: string; regulatoryFlags?: string[]; confidence?: number }[],
  focusColumn?: string,
): string {
  const sensitiveColumns = columns.filter(
    (c) => c.classificationLevel && c.classificationLevel !== 'PUBLIC' && c.classificationLevel !== 'INTERNAL',
  )

  const columnDetails = sensitiveColumns.map(
    (c) =>
      `  - ${c.columnName} (${c.dataType}, ${c.classificationLevel ?? 'UNCLASSIFIED'}, confidence: ${c.confidence ? Math.round(c.confidence * 100) : 0}%${c.regulatoryFlags && c.regulatoryFlags.length > 0 ? `, flags: ${c.regulatoryFlags.join('/')}` : ''})`,
  )

  const lines = [
    'You are helping create XACML access control policies based on data catalog information.',
    '',
    `TABLE: ${tableName}`,
    `Total columns: ${columns.length}`,
    `Sensitive columns: ${sensitiveColumns.length}`,
    '',
    ...(columnDetails.length > 0
      ? ['SENSITIVE COLUMNS:', ...columnDetails]
      : ['No sensitive columns detected.']),
    ...(focusColumn ? ['', `Focus on column: ${focusColumn}`] : []),
    '',
    'Help the user create XACML policies to protect this data.',
    'Use resource attributes in the format: resource.<schema>.<table>.<column>',
  ]

  return lines.join('\n')
}

// ============================================================================
// CONVERSATION REQUEST BUILDING
// ============================================================================

/**
 * Build a StartConversationRequest with cross-app context
 */
export function buildContextualConversationRequest(
  params: CrossAppParams,
  scan: DiscoveryScan,
  results: readonly DiscoveryResult[]
): StartConversationRequest {
  const systemPrompt = buildDiscoveryScanSystemPrompt(scan, results)

  return {
    contextType: 'discovery-scan',
    context: {
      appSource: 'discovery',
      contextData: {
        scanId: params.scanId,
        connectionName: params.connectionName,
        totalColumns: scan.columnsScanned,
        classifiedColumns: scan.columnsClassified,
        flaggedColumns: results.filter((r) => r.regulatoryFlags.length > 0).length,
      },
    },
    systemPrompt,
  }
}

// ============================================================================
// XACML SESSION STORAGE TRANSFER
// ============================================================================

/**
 * Store XACML content in sessionStorage for transfer to policy creation
 */
export function storeXacmlForPolicyCreation(xacml: string): void {
  try {
    sessionStorage.setItem(XACML_STORAGE_KEY, xacml)
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Unknown error'
    throw new Error(`Failed to store XACML for policy creation: ${message}`)
  }
}

/**
 * Retrieve and remove XACML content from sessionStorage
 * Returns null if no content is stored
 */
export function retrieveXacmlForPolicyCreation(): string | null {
  try {
    const xacml = sessionStorage.getItem(XACML_STORAGE_KEY)
    if (xacml) {
      sessionStorage.removeItem(XACML_STORAGE_KEY)
    }
    return xacml
  } catch {
    return null
  }
}
